import React, { useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";

// Get the financial year string for a given date
const getFinancialYear = (dateStr) => {
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = date.getMonth();
  const fyStart = month >= 3 ? year : year - 1;
  return `FY ${fyStart}-${fyStart + 1}`;
};

// Get unique financial years from project list, sorted descending
const getFinancialYears = (projects, includePreviousFY = false) => {
  const fySet = new Set();
  let minFYYear = new Date().getFullYear();

  projects.forEach((p) => {
    const date = new Date(p.start_date);
    const year = date.getFullYear();
    const month = date.getMonth();
    const fyStart = month >= 3 ? year : year - 1;
    const fyLabel = `FY ${fyStart}-${fyStart + 1}`;

    fySet.add(fyLabel);
    if (fyStart < minFYYear) {
      minFYYear = fyStart;
    }
  });

  if (includePreviousFY) {
    fySet.add(`FY ${minFYYear - 1}-${minFYYear}`);
  }

  return [...fySet].sort((a, b) => {
    const aStart = +a.split(" ")[1].split("-")[0];
    const bStart = +b.split(" ")[1].split("-")[0];
    return bStart - aStart;
  });
};

const CustomerBarChart = ({ projects }) => {
  const financialYears = useMemo(() => getFinancialYears(projects), [projects]);
  const currentFY = getFinancialYear(new Date());
  const [selectedFY, setSelectedFY] = useState(currentFY);

  const filteredProjects = useMemo(() => {
    if (!selectedFY) return projects;
    return projects.filter(
      (p) => getFinancialYear(p.start_date) === selectedFY
    );
  }, [projects, selectedFY]);

  // Aggregate project counts by customer
  const customerCounts = filteredProjects.reduce((acc, project) => {
    const customer = project.customer_name;
    acc[customer] = (acc[customer] || 0) + 1;
    return acc;
  }, {});

  // Prepare data for ECharts
  const customers = Object.keys(customerCounts);
  const projectCounts = Object.values(customerCounts);
  const generateColor = (index) => {
    const baseColors = ["#4BD5CE", "#FEC941", "#6482C3", "#197FC7", "#56B6FC"];
    if (index < baseColors.length) return baseColors[index];
    const hueShift = (index * 137) % 360;
    return `hsl(${hueShift}, 60%, 65%)`;
  };

  const colorMapping = {};
  customers.forEach((customer, index) => {
    colorMapping[customer] = generateColor(index);
  });

  const option = {
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
    },
    grid: {
      left: "3%",
      right: "4%",
      bottom: "8%",
      containLabel: true,
    },
    xAxis: [
      {
        type: "category",
        data: customers,
        axisTick: { alignWithLabel: true },
        axisLabel: {
          fontFamily: '"Lato", sans-serif',
          fontSize: 11,
          color: "#000",
        },
      },
    ],
    yAxis: [
      {
        type: "value",
        axisLabel: {
          fontFamily: '"Lato", sans-serif',
          fontSize: 13,
          color: "#000",
        },
      },
    ],
    series: [
      {
        name: "Projects",
        type: "bar",
        barWidth: "60%",
        data: projectCounts.map((count, index) => ({
          value: count,
          itemStyle: { color: colorMapping[customers[index]] },
          label: {
            show: true,
            position: "top",
            color: "#000",
            fontSize: 14,
            fontFamily: '"Lato", sans-serif',
          },
        })),
      },
    ],
  };

  return (
    <div className="project-bar-status">
      <h2 className="pie-header">Projects by Customer</h2>
      <select
        className="pm-fy-dd"
        value={selectedFY}
        onChange={(e) => setSelectedFY(e.target.value)}
      >
        <option value="">All Years</option>
        {financialYears.map((fy) => (
          <option key={fy} value={fy}>
            {fy}
          </option>
        ))}
      </select>
      <p className="bar-chart-text">Number of Projects</p>
      <p className="bar-chart-text-bottom">Customer</p>
      <ReactECharts
        className="customer"
        option={option}
        style={{
          height: "420px",
          width: "98%",
          top: "-1rem",
          backgroundColor: "transparent",
          left: "1rem",
        }}
      />
    </div>
  );
};

export default CustomerBarChart;
